import { useMutation, useQueryClient } from '@tanstack/react-query'
import { client } from '../client'
import { queryKeys } from '../keys'
import type { components } from '@/lib/generated/api-types'

type WebhookCreate = components['schemas']['WebhookCreate']
type WebhookUpdate = components['schemas']['WebhookUpdate']
type WebhookTestRequest = components['schemas']['WebhookTestRequest']

// Create webhook
export function useCreateWebhook() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (webhook: WebhookCreate) => {
      const { data, error } = await client.POST('/api/v1/webhooks', {
        body: webhook,
      })
      if (error) throw error
      return data
    },
    onSuccess: () => {
      // Invalidate webhooks list to refetch
      queryClient.invalidateQueries({
        queryKey: queryKeys.webhooks.all,
      })
    },
  })
}

// Update webhook
export function useUpdateWebhook() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      webhookId,
      updates,
    }: {
      webhookId: number
      updates: WebhookUpdate
    }) => {
      const { data, error } = await client.PUT(
        '/api/v1/webhooks/{webhook_id}',
        {
          params: { path: { webhook_id: String(webhookId) } },
          body: updates,
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: (_data, { webhookId }) => {
      // Invalidate specific webhook and list
      queryClient.invalidateQueries({
        queryKey: queryKeys.webhooks.detail(String(webhookId)),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.webhooks.all,
      })
    },
  })
}

// Delete webhook
export function useDeleteWebhook() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (webhookId: number) => {
      const { error } = await client.DELETE('/api/v1/webhooks/{webhook_id}', {
        params: { path: { webhook_id: String(webhookId) } },
      })
      if (error) throw error
    },
    onSuccess: (_data, webhookId) => {
      // Remove webhook from cache
      queryClient.removeQueries({
        queryKey: queryKeys.webhooks.detail(String(webhookId)),
      })
      // Invalidate list
      queryClient.invalidateQueries({
        queryKey: queryKeys.webhooks.all,
      })
    },
  })
}

// Toggle webhook status
export function useToggleWebhook() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (webhookId: number) => {
      const { data, error } = await client.PATCH(
        '/api/v1/webhooks/{webhook_id}/toggle',
        {
          params: { path: { webhook_id: String(webhookId) } },
        },
      )
      if (error) throw error
      return data
    },
    onSuccess: (_data, webhookId) => {
      // Invalidate specific webhook and list
      queryClient.invalidateQueries({
        queryKey: queryKeys.webhooks.detail(String(webhookId)),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.webhooks.all,
      })
    },
  })
}

// Test webhook
export function useTestWebhook() {
  return useMutation({
    mutationFn: async ({
      webhookId,
      eventType,
    }: {
      webhookId: number
      eventType?: string
    }) => {
      const body: WebhookTestRequest = eventType
        ? { event_type: eventType }
        : { event_type: null }
      const { data, error } = await client.POST(
        '/api/v1/webhooks/{webhook_id}/test',
        {
          params: { path: { webhook_id: String(webhookId) } },
          body,
        },
      )
      if (error) throw error
      return data
    },
  })
}
